import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Mail } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { ProfilePhoto } from "@/components/site/ProfilePhoto";

// Nav model. `children` = in-place expandable sub-items.
const LINKS = [
    { to: "/", label: "Home", testid: "site-nav-home-link", end: true },
    { to: "/about", label: "About", testid: "site-nav-about-link" },
    {
        to: "/research",
        label: "Research",
        testid: "site-nav-research-link",
        children: [
            { to: "/research#publications", label: "Publications", testid: "site-nav-research-publications" },
            { to: "/research#working-papers", label: "Working Papers", testid: "site-nav-research-working" },
            { to: "/research#presentations", label: "Presentations", testid: "site-nav-research-presentations" },
        ],
    },
    {
        to: "/teaching",
        label: "Teaching",
        testid: "site-nav-teaching-link",
        children: [
            { to: "/teaching#courses", label: "Courses", testid: "site-nav-teaching-courses" },
            { to: "/teaching#materials", label: "Case Studies & Materials", testid: "site-nav-teaching-materials" },
        ],
    },
    { to: "/cv", label: "CV", testid: "site-nav-cv-link" },
    { to: "/contact", label: "Contact", testid: "site-nav-contact-link" },
];

// -----------------------------------------------------------------------------
// Exported `Nav` — keeps the existing App.js import working.
// Renders: mobile top-bar + slide-out drawer AND desktop fixed left sidebar.
// -----------------------------------------------------------------------------
export function Nav() {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    useEffect(() => { setOpen(false); }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    return (
        <>
            {/* Mobile top bar (hidden on lg+ where sidebar is visible) */}
            <MobileTopBar onOpen={() => setOpen(true)} />

            {/* Desktop fixed sidebar */}
            <aside
                className="hidden lg:flex fixed inset-y-0 left-0 z-40 w-[300px] bg-sidebar border-r border-border flex-col"
                aria-label="Primary navigation"
                data-testid="site-sidebar"
            >
                <SidebarInner pathname={location.pathname} />
            </aside>

            {/* Mobile drawer (portal) */}
            <MobileDrawer open={open} onClose={() => setOpen(false)} pathname={location.pathname} />
        </>
    );
}

// -----------------------------------------------------------------------------
// SidebarInner — the panel content shared by desktop sidebar & mobile drawer
// -----------------------------------------------------------------------------
function SidebarInner({ pathname, onNavigate }) {
    // Scroll progress bar at the bottom
    const { scrollYProgress } = useScroll();
    const barWidth = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

    // Which parent link (with children) is expanded in place. Auto-expand the
    // one that matches the current route so users always see relevant sub-items.
    const initiallyExpanded = useMemo(() => {
        const active = LINKS.find((l) => l.children && pathname.startsWith(l.to));
        return active ? active.to : null;
    }, [pathname]);
    const [expanded, setExpanded] = useState(initiallyExpanded);
    useEffect(() => { setExpanded(initiallyExpanded); }, [initiallyExpanded]);

    return (
        <div className="flex flex-col h-full">
            {/* Identity block */}
            <div className="px-6 pt-10 pb-8 flex flex-col items-center text-center">
                <Link
                    to="/"
                    onClick={onNavigate}
                    className="block h-32 w-32 rounded-full overflow-hidden border-2 border-white shadow-[0_10px_30px_-10px_rgba(17,24,39,0.25)] bg-white"
                    data-testid="site-nav-brand-link"
                    aria-label="Home — Aditya Baheti"
                >
                    <ProfilePhoto className="h-full w-full object-cover" alt="Aditya Baheti" />
                </Link>
                <p className="mt-5 font-display text-2xl leading-tight text-foreground">Aditya Baheti</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.24em] text-muted-foreground">
                    Academic Fellow
                </p>
            </div>

            <div className="mx-6 h-px bg-border" />

            {/* Nav list */}
            <nav className="flex-1 overflow-y-auto px-3 py-6" aria-label="Primary">
                <ul className="space-y-1">
                    {LINKS.map((l) => (
                        <SidebarItem
                            key={l.to}
                            link={l}
                            pathname={pathname}
                            expanded={expanded === l.to}
                            onToggle={() => setExpanded(expanded === l.to ? null : l.to)}
                            onNavigate={onNavigate}
                        />
                    ))}
                </ul>
            </nav>

            {/* Footer strip */}
            <div className="px-6 pb-6 pt-2 border-t border-border">
                <a
                    href="mailto:adibaheti07@gmail.com"
                    className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground hover:text-accent transition-colors"
                    data-testid="site-nav-email"
                >
                    <Mail className="h-3.5 w-3.5" /> adibaheti07@gmail.com
                </a>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/70">
                    National Law University · Jodhpur
                </p>
                <motion.div style={{ width: barWidth }} className="mt-4 h-[2px] bg-accent origin-left rounded-full" />
            </div>
        </div>
    );
}

// -----------------------------------------------------------------------------
// SidebarItem — a single link (optionally with expandable sub-menu)
// -----------------------------------------------------------------------------
function SidebarItem({ link, pathname, expanded, onToggle, onNavigate }) {
    const hasChildren = !!link.children;
    const isActive = link.end ? pathname === link.to : pathname.startsWith(link.to);

    // Shared button/link classes for the row itself
    const rowBase =
        "relative flex items-center w-full pl-5 pr-3 py-2.5 rounded-lg text-[15px] font-medium transition-colors";
    const rowActive = "text-accent";
    const rowIdle = "text-foreground/75 hover:text-foreground hover:bg-white/60";

    return (
        <li>
            <div className="flex items-center gap-1">
                <NavLink
                    to={link.to}
                    end={link.end}
                    onClick={onNavigate}
                    data-testid={link.testid}
                    className={({ isActive: navActive }) => `${rowBase} ${navActive ? rowActive : rowIdle} flex-1`}
                >
                    {({ isActive: navActive }) => (
                        <>
                            {/* Left-edge accent bar */}
                            {navActive && (
                                <motion.span
                                    layoutId="sidebar-active-bar"
                                    className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-[3px] rounded-r bg-accent"
                                    aria-hidden
                                />
                            )}
                            <span className={navActive ? "font-semibold" : ""}>{link.label}</span>
                        </>
                    )}
                </NavLink>
                {hasChildren && (
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-label={`Toggle ${link.label} submenu`}
                        aria-expanded={expanded}
                        data-testid={`${link.testid}-toggle`}
                        className={`h-9 w-9 grid place-items-center rounded-lg transition-colors ${
                            isActive ? "text-accent" : "text-muted-foreground hover:text-foreground hover:bg-white/60"
                        }`}
                    >
                        <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </button>
                )}
            </div>

            <AnimatePresence initial={false}>
                {hasChildren && expanded && (
                    <motion.ul
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden pl-9 pr-3 pb-1"
                    >
                        {link.children.map((c) => {
                            const isChildActive =
                                pathname === c.to.split("#")[0] &&
                                (window.location.hash === (c.to.split("#")[1] ? `#${c.to.split("#")[1]}` : "") ||
                                    !c.to.includes("#"));
                            return (
                                <li key={c.to}>
                                    <Link
                                        to={c.to}
                                        onClick={onNavigate}
                                        data-testid={c.testid}
                                        className={`flex items-center gap-2 py-2 text-sm transition-colors ${
                                            isChildActive
                                                ? "text-accent font-medium"
                                                : "text-muted-foreground hover:text-foreground"
                                        }`}
                                    >
                                        <span
                                            className={`h-1.5 w-1.5 rounded-full ${
                                                isChildActive ? "bg-accent" : "bg-muted-foreground/40"
                                            }`}
                                            aria-hidden
                                        />
                                        {c.label}
                                    </Link>
                                </li>
                            );
                        })}
                    </motion.ul>
                )}
            </AnimatePresence>
        </li>
    );
}

// -----------------------------------------------------------------------------
// Mobile top bar
// -----------------------------------------------------------------------------
function MobileTopBar({ onOpen }) {
    return (
        <header className="lg:hidden sticky top-0 z-40 backdrop-blur-md bg-background/90 border-b border-border">
            <div className="h-14 px-4 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3" data-testid="site-nav-brand-link-mobile">
                    <span className="h-8 w-8 rounded-full overflow-hidden bg-secondary">
                        <ProfilePhoto className="h-full w-full object-cover" alt="Aditya Baheti" />
                    </span>
                    <span className="flex items-baseline gap-2">
                        <span className="font-display text-base tracking-tight text-foreground">Aditya Baheti</span>
                    </span>
                </Link>
                <button
                    onClick={onOpen}
                    aria-label="Open menu"
                    className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-border hover:bg-secondary transition-colors"
                    data-testid="site-nav-mobile-toggle"
                >
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Menu</span>
                    <Menu className="h-4 w-4" />
                </button>
            </div>
        </header>
    );
}

// -----------------------------------------------------------------------------
// Mobile drawer — reuses the same SidebarInner so structure/state stays in sync
// -----------------------------------------------------------------------------
function MobileDrawer({ open, onClose, pathname }) {
    if (typeof document === "undefined") return null;
    return createPortal(
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        onClick={onClose}
                        className="fixed inset-0 z-[80] bg-foreground/40 backdrop-blur-sm lg:hidden"
                        aria-hidden
                    />
                    <motion.div
                        key="panel"
                        initial={{ x: "-100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="fixed inset-y-0 left-0 z-[90] w-[86%] max-w-[320px] bg-sidebar border-r border-border shadow-[0_0_60px_-10px_rgba(17,24,39,0.35)] lg:hidden overflow-hidden"
                        data-testid="site-nav-mobile-drawer"
                    >
                        <div className="relative h-full">
                            <button
                                onClick={onClose}
                                aria-label="Close menu"
                                className="absolute top-4 right-4 z-10 h-9 w-9 rounded-full grid place-items-center border border-border bg-white/70 hover:bg-white transition-colors"
                                data-testid="site-nav-mobile-close"
                            >
                                <X className="h-4 w-4" />
                            </button>
                            <SidebarInner pathname={pathname} onNavigate={onClose} />
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>,
        document.body,
    );
}
